import { tap, filter, map, takeUntil } from "rxjs/operators";
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import {
  CustomerResponseInterface,
  FileRecord,
  MemberResponseInterface,
  PendingInvitationResponseInterface
} from "@careapp/angular-sdk";
import {
  CARER_ROLE,
  CUSTOMER_CARER_ROLE,
  CUSTOMER_FAMILY_ROLE,
  FAMILY_ROLE
} from "@careapp/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";

import { FormMode } from "../../shared/constants";
import * as actions from "../../store/actions/index";
import { RequestLocation } from "../../store/actions/location.actions";
import * as selectors from "../../store/reducers/index";
import { MatDialog } from "@angular/material";
import { AddToTeamDialog } from "./add-to-team/add-to-team-dialog.component";

import * as moment from "moment-timezone";
import { InviteDataSource } from "../staff-list/staff-list.component";
import { getInvitesForCustomer } from "../../store/selectors/invite.selectors";

@Component({
  selector: "customer-component",
  templateUrl: "./customer.component.html",
  styleUrls: ["customer.component.scss"]
})
export class CustomerComponent implements OnInit {
  public loading: Observable<boolean>;
  public roleOptions = [CUSTOMER_FAMILY_ROLE, CUSTOMER_CARER_ROLE];
  id: string;
  mode = FormMode.EDIT;
  customer$: Observable<CustomerResponseInterface>;
  customerFiles$: Observable<FileRecord[]>;
  customerTeam$: Observable<MemberResponseInterface[]>;
  staff$: Observable<MemberResponseInterface[]>;
  token$: Observable<string>;
  location$: Observable<any>;
  locationLoading$: Observable<boolean>;
  updatedProfileImage: any;
  uploading$: Observable<boolean>;
  form = new FormGroup({
    staff: new FormControl("", Validators.required),
    role: new FormControl("", Validators.required)
  });

  staffTableConfig = [
    { key: "firstName", title: "First Name" },
    { key: "lastName", title: "Last Name" },
    { key: "email", title: "Email" },
    { key: "action", title: "Action" }
  ];
  public inviteConfig = [
    {
      key: "email",
      sortable: false,
      title: "Email",
      render: (row: PendingInvitationResponseInterface) => (row && row.to) || ""
    },
    {
      key: "role",
      sortable: false,
      title: "Role",
      render: (row: PendingInvitationResponseInterface) =>
        (row && row.role) || ""
    },
    {
      key: "date",
      sortable: false,
      title: "Sent",
      render: (row: PendingInvitationResponseInterface) =>
        row && row.token ? moment.unix(row.token.iat).fromNow() : ""
    },
    {
      key: "exp",
      sortable: false,
      title: "Expiration",
      render: (row: PendingInvitationResponseInterface) =>
        row && row.token ? moment.unix(row.token.exp).fromNow() : ""
    },
    {
      key: "actions",
      sortable: false,
      title: "Actions"
    }
  ];

  public invites: InviteDataSource;

  constructor(
    private store: Store<any>,
    public route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    const id = (this.id = this.route.snapshot.params.customerId);
    this.loading = this.store
      .select(selectors.getLoadingEntities("customers"))
      .pipe(map(state => state[id]));
    this.store.dispatch(actions.Customers.SelectByIdAction({ id }));
    this.store.dispatch(actions.Customers.RequestByIdAction({ id }));
    this.store.dispatch(actions.CustomerFiles.RequestAllAction);
    this.store.dispatch(actions.CustomerTeam.RequestAllAction);
    this.store.dispatch(actions.Staff.RequestAllAction);
    this.customer$ = this.store.select(
      selectors.getSelected(selectors.CUSTOMERS)
    );
    this.customerFiles$ = this.customer$.pipe(
      map(customer => (customer && customer.files) || [])
    );
    this.customerTeam$ = this.store.select(
      selectors.getAll(selectors.CUSTOMER_TEAM)
    );
    this.staff$ = this.store.select(
      selectors.getSearchResults(selectors.STAFF)
    );
    this.token$ = this.store.select(selectors.auth.getAuthToken);
    this.location$ = this.store.select(selectors.getLocation);
    this.locationLoading$ = this.store.select(selectors.isLocationLoading);

    this.invites = new InviteDataSource(
      this.store.select(getInvitesForCustomer)
    );
    this.store.dispatch(new actions.RequestPendingInvitations());
  }

  resendInvite(id: string) {
    this.store.dispatch(new actions.RequestInvitationResendAction({ id }));
  }
  cancelInvite(id: string) {
    this.store.dispatch(new actions.RequestInvitationCancelAction({ id }));
  }

  selectStaff(staff: any) {
    this.form.get("staff").setValue(staff);
  }

  showAddTeamDialog() {
    let dialogRef = this.dialog.open(AddToTeamDialog, {
      data: this.id
    });
    dialogRef.afterClosed().subscribe(() => {
      this.store.dispatch(new actions.RequestPendingInvitations());
    });
  }

  get staffName() {
    const staff = this.form.get("staff").value;
    if (staff) {
      return `${staff.firstName} ${staff.lastName}`;
    }
    return "";
  }

  submitCustomer(customer: any) {
    const data = { ...customer, profileImage: this.updatedProfileImage };
    data.profileImage = this.store.dispatch(
      actions.Customers.RequestSaveAction({
        id: this.route.snapshot.params.customerId,
        data: customer
      })
    );
  }

  uploadFile(file: any) {
    const id = Math.random().toString(36);
    this.store.dispatch(
      new actions.RequestFileUploadStartAction({
        id,
        file,
        type: `customer`
      })
    );

    this.uploading$ = this.store.select(selectors.getFileUploadState).pipe(
      map(files => files[id].progress < 100),
      takeUntil(
        this.store.select(selectors.getFileUploadState).pipe(
          map(files => files[id].error || files[id].success),
          filter(val => !!val),
          tap(files => {
            if (files[id] && files[id].response) {
              this.updatedProfileImage = files[id].response;
            }
          })
        )
      )
    );
  }

  addMember() {
    if (this.form.valid) {
      this.store.dispatch(
        actions.CustomerTeam.RequestCreateAction(this.form.value)
      );
    }
  }

  searchStaff(search) {
    console.log(search);
    let or = [];

    if (!search.trim()) {
      return;
    }

    search
      .split(" ")
      .filter(term => !!term || !!term.trim())
      .forEach(term => {
        or = or.concat(
          ["firstName", "lastName", "email"].map(key => ({
            [key]: { ilike: `${term.trim()}%` }
          }))
        );
      });

    this.store.dispatch(actions.Staff.RequestSearchAction({ where: { or } }));
  }

  removeMember(item) {
    this.store.dispatch(actions.CustomerTeam.RequestRemove(item));
  }

  renderStaff(item: MemberResponseInterface) {
    return item ? `${item.lastName}, ${item.firstName}` : ``;
  }

  removeFile(file: any) {
    return this.store.dispatch(
      actions.CustomerFiles.RequestDestroyByIdAction(file)
    );
  }

  filterStaff(search: string, item: MemberResponseInterface) {
    return (
      item.firstName.startsWith(search) || item.lastName.startsWith(search)
    );
  }

  locationRequest(address) {
    this.store.dispatch(new RequestLocation(address));
  }

  invite(data) {
    const permissions = [
      {
        modelId: this.id,
        modelName: "Customer",
        role: data.role
      }
    ];
    let role;
    switch (data.role) {
      case CUSTOMER_CARER_ROLE:
        role = CARER_ROLE;
        break;
      case CUSTOMER_FAMILY_ROLE:
        role = FAMILY_ROLE;
        break;
      default:
        return;
    }
    this.store.dispatch(
      new actions.RequestInvitationSendAction({
        email: data.email,
        role,
        permissions
      })
    );
  }
}
